import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { PostalAddress } from '@/lib/address/address-rules'

/**
 * What a partner is to the tenant (z8nrz7earp): a fixed list, in this order.
 * A partner is exactly one; `Customer` is what the customer pickers list.
 */
export const PARTNER_CATEGORIES = ['Customer', 'Vendor', 'Courier', 'Supplier', 'Partner'] as const
export type PartnerCategory = (typeof PARTNER_CATEGORIES)[number]

/**
 * A tenant's partner (z8nrz7earp), shown as Partners and created as a customer
 * in z8nrz7e6zn — the type, route and permission keys keep that name. `code` is
 * the user-entered number, unique within the tenant.
 */
export interface Customer {
  id: string
  code: string
  name: string
  /** Absent from a server older than Partners, where every row is a Customer. */
  category?: PartnerCategory
  /** No longer on the form; kept so nothing entered is lost. */
  description: string | null
  /** The street line of the address. */
  address: string | null
  country?: string | null
  state?: string | null
  city?: string | null
  zipCode?: string | null
  contactPerson: string | null
  phone: string | null
  email: string | null
  /**
   * The structured address (z8nrz7epeq): the stored one, or for a partner saved
   * before structured addresses, its flat fields mapped to this shape. Null when
   * it has none; absent from an older server.
   */
  postalAddress?: PartnerAddressView | null
  createdAt: string
  updatedAt: string
}

/** A partner's address as the API returns it. */
export type PartnerAddressView = PostalAddress & {
  /** False while it is still read from the partner's flat fields. */
  stored: boolean
  /** It breaks its country's rules: shown as "Needs review". */
  needsReview: boolean
}

/**
 * Body for create and update: what the form edits. A blank contact detail is
 * sent as '' and stored as empty, so an edit can clear it. Description is not
 * sent, so an edit leaves any a partner already has untouched.
 *
 * `postalAddress` (z8nrz7epeq) is checked against its country's rules by the
 * server too. Left out, the stored address is left as it is; null removes it.
 */
export interface CustomerPayload {
  code: string
  name: string
  /** '' only while the form has none picked; the form refuses to save that. */
  category: PartnerCategory | ''
  contactPerson: string
  phone: string
  email: string
  postalAddress?: PostalAddress | null
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const body = JSON.parse(text) as { message?: string | string[] }
      // class-validator answers with an array of messages; show them all.
      message = Array.isArray(body.message) ? body.message.join('. ') : (body.message ?? text)
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

// Tenant-scoped: authHeaders injects X-Tenant-Id for the selected tenant.
export const customersApi = {
  /** By code. With `categories`, only partners in one of them. */
  list: (params: { categories?: readonly PartnerCategory[] } = {}) => {
    const qs = new URLSearchParams()
    for (const c of params.categories ?? []) qs.append('category', c)
    const q = qs.toString()
    return request<Customer[]>(`/customers${q ? `?${q}` : ''}`)
  },

  create: (payload: CustomerPayload) =>
    request<Customer>('/customers', { method: 'POST', body: JSON.stringify(payload) }),

  update: (id: string, payload: CustomerPayload) =>
    request<Customer>(`/customers/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  remove: (id: string) => request<{ ok: true }>(`/customers/${id}`, { method: 'DELETE' }),
}
